import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const PURPLE = '#9c27b0';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#8f8f8f';
const MONO = '"JetBrains Mono", monospace';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    secondary: { main: PURPLE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: { fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' },
});

function Section({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return <Box component="section" sx={{ py: { xs: 8, md: 12 }, ...sx }}><Container maxWidth="lg">{children}</Container></Box>;
}

function SectionChip({ label, color }: { label: string; color: string }) {
  return <Chip label={label} size="small" sx={{ mb: 2, bgcolor: `${color}18`, color, border: `1px solid ${color}4d`, fontWeight: 700, letterSpacing: 0.5 }} />;
}

export default function HowAithonWorksPage() {
  const router = useRouter();

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>How Aithon Works — Transparent Commerce for Business Services</title>
        <meta name="description" content="See how Aithon works: visible pricing, open perk competition, trust score, renewals, and the structured commerce layer for partners, buyers, and AI agents." />
        <link rel="canonical" href="https://aithon.tech/how-aithon-works" />
        <meta property="og:title" content="How Aithon Works — Transparent Commerce for Business Services" />
        <meta property="og:description" content="Visible pricing, transparent ranking, trust score, and structured commerce for buyers, partners, and AI agents." />
      </Head>
      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        <Box sx={{ pt: { xs: 12, md: 18 }, pb: { xs: 8, md: 14 }, background: 'radial-gradient(ellipse at 25% 20%, rgba(33,150,243,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(156,39,176,0.06) 0%, transparent 50%)' }}>
          <Container maxWidth="lg">
            <Typography component="a" href="/about" sx={{ color: TEXT_DIM, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 4, '&:hover': { color: ELECTRIC_BLUE } }}>
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to About
            </Typography>
            <Typography variant="h1" sx={{ fontWeight: 900, fontSize: { xs: '2.45rem', md: '4.2rem' }, lineHeight: 1.08, mb: 3, maxWidth: 900 }}>
              How Aithon makes business services buyable.
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: { xs: '1.08rem', md: '1.28rem' }, lineHeight: 1.7, maxWidth: 860, mb: 4 }}>
              The mechanics are simple: show the real price, let providers compete with visible perks,
              rank them by delivered value, and let trust compound over time.
            </Typography>
          </Container>
        </Box>

        <Section>
          <SectionChip label="THE FLOW" color={ELECTRIC_BLUE} />
          <Grid container spacing={3}>
            {[
              ['1. Buyers search once', 'A buyer enters the category and location. Aithon shows live services, prices, perks, and provider ranking.'],
              ['2. Providers compete in the open', 'Partners attach perks to win the listing — audits, setup, credits, bundles, or ongoing value.'],
              ['3. Aithon scores the offers', 'Perk Value Score, Trust Score, and SLA compliance determine which offer ranks first.'],
              ['4. The order closes', 'The provider gets the customer. The partner earns commission. The buyer gets the promised perk.'],
              ['5. Performance compounds', 'On-time delivery and successful deals build Trust Score, improving future ranking and platform-wide promotion.'],
            ].map(([title, desc]) => (
              <Grid item xs={12} md={6} key={title}>
                <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                  <Typography sx={{ color: ELECTRIC_BLUE, fontFamily: MONO, fontWeight: 700, mb: 1 }}>{title}</Typography>
                  <Typography sx={{ color: TEXT_DIM, lineHeight: 1.75 }}>{desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <SectionChip label="WHY THIS IS DIFFERENT" color={ORANGE} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 4 }}>
            Old way vs. Aithon
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                <Typography sx={{ color: ORANGE, fontWeight: 700, mb: 2 }}>OLD WAY</Typography>
                <Stack spacing={1.5}>
                  {[
                    'Opaque pricing',
                    'Phone calls and forms',
                    'Whoever reaches the buyer first usually wins',
                    'No structured comparison layer',
                    'No machine-readable catalog for AI agents',
                    'Trust is anecdotal, hidden, and hard to verify',
                  ].map((item) => <Typography key={item} sx={{ color: TEXT_DIM }}>• {item}</Typography>)}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.3)', height: '100%' }}>
                <Typography sx={{ color: ELECTRIC_BLUE, fontWeight: 700, mb: 2 }}>AITHON</Typography>
                <Stack spacing={1.5}>
                  {[
                    'Visible pricing and perks',
                    'Open competition in one marketplace',
                    'Providers win by delivered value, not timing',
                    'Trust Score and SLA data shape ranking',
                    'Structured commerce for both humans and AI agents',
                    'Buyers can compare before they ever talk to anyone',
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.25} alignItems="flex-start">
                      <CheckIcon sx={{ color: GREEN, fontSize: 18, mt: '3px' }} />
                      <Typography sx={{ color: TEXT }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ═══ THE OPENING ACT ═══ */}
        <Section>
          <SectionChip label="THE OPENING ACT" color={ORANGE} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.6rem' }, mb: 6, color: '#fff' }}>
            The commission pays for your sales funnel.
          </Typography>

          {/* Beat 1 — Apartment Story */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{ fontFamily: MONO, color: ORANGE, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2 }}>
              Beat 1 — The Apartment Story
            </Typography>
            <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: BG_SURFACE, borderLeft: `4px solid ${ORANGE}` }}>
              <Typography sx={{ color: TEXT, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.8, fontStyle: 'italic' }}>
                &ldquo;When I was apartment hunting, one locator said: &lsquo;We&apos;ll cover your
                moving costs if you sign here.&rsquo; I stopped shopping immediately. Behind the
                scenes, they were splitting their leasing commission with the moving company. The
                customer won without knowing the mechanics.&rdquo;
              </Typography>
            </Box>
          </Box>

          {/* Beat 2 — Band Analogy */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{ fontFamily: MONO, color: ORANGE, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2 }}>
              Beat 2 — The Band Analogy
            </Typography>
            <Typography sx={{ color: TEXT, fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 800 }}>
              Your managed security firm is the opening act. AT&amp;T internet is the headliner.
              Nobody&apos;s buying tickets to see the opening act — but once they&apos;re in the
              venue, some of them become fans. Attach your service as a Perk on a mainstream carrier
              listing. Ride the headliner&apos;s traffic. Build your own audience.
            </Typography>
          </Box>

          {/* Beat 3 — The Math */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{ fontFamily: MONO, color: ORANGE, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase', mb: 2 }}>
              Beat 3 — The Math
            </Typography>
            <Box sx={{ bgcolor: BG_SURFACE, border: `1px solid rgba(255, 152, 0, 0.2)`, borderRadius: 3, p: { xs: 3, md: 4 }, maxWidth: 600 }}>
              {[
                { label: 'Win the top listing on a Comcast circuit', value: '~$800 commission', highlight: false },
                { label: 'Deliver a free 30-day security assessment', value: '~$300 cost', highlight: false },
                { label: 'Net from the circuit alone', value: '~$500 profit', highlight: true },
                { label: 'Assessment converts to managed contract', value: '$2,000/mo recurring', highlight: true },
                { label: 'Customer acquisition cost', value: '$0', highlight: true },
              ].map((row, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  <Typography sx={{ color: TEXT_DIM, fontSize: '0.95rem' }}>{row.label}</Typography>
                  <Typography sx={{ fontFamily: MONO, fontWeight: row.highlight ? 800 : 500, color: row.highlight ? GREEN : TEXT, fontSize: row.highlight ? '1.05rem' : '0.95rem' }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.85rem', mt: 2, lineHeight: 1.6 }}>
                The carrier commission funded your entire sales funnel. The customer got a free
                assessment. You got a $2k/mo client.
              </Typography>
            </Box>
          </Box>

          {/* Old vs Aithon model */}
          <Grid container spacing={3} sx={{ mb: 5, maxWidth: 800 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: BG_SURFACE, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                <Typography sx={{ color: TEXT_DIM, fontFamily: MONO, fontSize: '0.75rem', letterSpacing: 1, mb: 2, textTransform: 'uppercase' }}>
                  Old Model
                </Typography>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.95rem', lineHeight: 1.7 }}>
                  Cold outreach → demo → proposal → close.<br />
                  60–90 day sales cycle.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: BG_SURFACE, border: `1px solid rgba(0, 230, 118, 0.2)`, height: '100%' }}>
                <Typography sx={{ color: GREEN, fontFamily: MONO, fontSize: '0.75rem', letterSpacing: 1, mb: 2, textTransform: 'uppercase' }}>
                  Aithon Model
                </Typography>
                <Typography sx={{ color: TEXT, fontSize: '0.95rem', lineHeight: 1.7 }}>
                  Win the top listing → customer is already bought in → upsell from trust.<br />
                  Sales cycle starts post-install.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Closing callout */}
          <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', maxWidth: 800 }}>
            <Typography sx={{ color: ORANGE, fontSize: '1rem', fontStyle: 'italic', lineHeight: 1.7 }}>
              Try both. Cash perks and service perks. But definitely try the Opening Act. You might
              be surprised what the commission covers.
            </Typography>
          </Box>
        </Section>

        <Section>
          <SectionChip label="TRUST SCORE" color={GREEN} />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.45rem' }, mb: 3 }}>
            You don&apos;t start from zero.
          </Typography>
          <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8, fontSize: '1.05rem', maxWidth: 900, mb: 4 }}>
            Trust Score is based on real commerce signals: successful deals, perk delivery, SLA compliance,
            repeat buyers, and transaction health. It is not a vanity review counter.
          </Typography>
          <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: 'rgba(0,230,118,0.07)', border: '1px solid rgba(0,230,118,0.22)', maxWidth: 920, mb: 4 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>Bring your existing momentum.</Typography>
            <Typography sx={{ color: TEXT_DIM, lineHeight: 1.75 }}>
              If you already have transaction history, carrier relationships, or a track record of fulfillment,
              that history informs your starting position. Aithon is built to recognize existing credibility so the best operators don&apos;t start at the same line as empty accounts.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              ['Transaction activity', 'Are you active in the market?'],
              ['Success rate', 'Do deals actually close?'],
              ['SLA compliance', 'Did you deliver on time?'],
              ['Repeat buyers', 'Do customers come back?'],
              ['Buyer diversity', 'Can you win across the market?'],
            ].map(([title, desc]) => (
              <Grid item xs={12} sm={6} md={4} key={title}>
                <Box sx={{ p: 2.5, borderRadius: 2.5, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                  <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem' }}>{desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <SectionChip label="ECONOMICS" color={ORANGE} />
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.45rem' }, mb: 3 }}>
                Why the pay-per-closed-sale model matters
              </Typography>
              <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8, mb: 2.5 }}>
                The buyer gets a better offer. The partner pays only when value actually changes hands.
                The marketplace can optimize for delivered outcomes instead of empty lead volume.
              </Typography>
              <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8 }}>
                That creates a better market for humans and for AI agents. Structured competition increases value for buyers while rewarding partners who can actually fulfill.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ color: ORANGE, fontFamily: MONO, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 1, mb: 2.5 }}>One sample deal</Typography>
                {[
                  ['Service price', '$10,000/mo'],
                  ['Commission (15% residual)', '$1,500/mo'],
                  ['Aithon keeps (10%)', '-$150/mo'],
                  ['You keep', '$1,350/mo'],
                  ['3-year value', '$48,600'],
                ].map(([label, value], index) => (
                  <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 1.35, borderBottom: index < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                    <Typography sx={{ color: TEXT_DIM }}>{label}</Typography>
                    <Typography sx={{ color: index >= 3 ? GREEN : TEXT, fontFamily: MONO, fontWeight: index >= 3 ? 800 : 500 }}>{value}</Typography>
                  </Stack>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Section>

        <Section>
          <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
            <SectionChip label="BUILDING AN AGENT?" color={PURPLE} />
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}>
              The market is structured. Your agent can actually use it.
            </Typography>
            <Typography sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
              Machine-readable catalog, one-call registration, and the same trust and economics layer as human partners.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => router.push('/for-agents')}>For Agents</Button>
              <Button variant="outlined" startIcon={<SmartToyIcon />} onClick={() => router.push('/for-partners')} sx={{ color: TEXT, borderColor: 'rgba(255,255,255,0.2)' }}>For Partners</Button>
            </Stack>
          </Box>
        </Section>
      </Box>
    </ThemeProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=7200');
  return { props: {} };
};
